import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { MoreVertical, LayoutDashboard, History, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getRetreatListings, getActiveCenters } from '../../Services/Retreats/RetreatApis';
import { categoryActiveApi, subCategoryActiveApiWithoutPagination } from '../../Services/CategoryManagement/CategoryManagementApi';
import GlobalPagination from '../../Components/Common/Globalpagination';
// Shared sx for all filter inputs
const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px',
        height: '36px',
        fontSize: '0.8125rem',
        '& fieldset': { borderColor: '#E8E8E8' },
        '&:hover fieldset': { borderColor: '#FFBD65' },
        '&.Mui-focused fieldset': { borderColor: '#FFBD65' },
    },
    '& .MuiInputBase-input': { py: '7px', fontSize: '0.8125rem' },
};

const Retreats = () => {
    const navigate = useNavigate();
    const [retreats, setRetreats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const [selectedCenter, setSelectedCenter] = useState(null);
    const [centerNameSearch, setCenterNameSearch] = useState('');
    const [centerSuggestions, setCenterSuggestions] = useState([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [limit, setLimit] = useState(10);
    const tableContainerRef = useRef(null);
    const [showScrollHint, setShowScrollHint] = useState(false);

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [subCategories, setSubCategories] = useState([]);
    const [selectedSubCategories, setSelectedSubCategories] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRetreat, setSelectedRetreat] = useState(null);
    const openMenu = Boolean(anchorEl);

    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(null, args), delay);
        };
    };

    const fetchRetreats = useCallback(async (searchQuery = '', status = '', locationQuery = '', centerIdQuery = '', currentPage = 1, categoryId = '', subCategoryId = '', currentLimit = limit) => {
        try {
            setLoading(true);
            const actualLimit = currentLimit !== 10 ? currentLimit : undefined;
            const response = await getRetreatListings(currentPage, actualLimit, searchQuery, status, locationQuery, centerIdQuery, categoryId, subCategoryId);
            if (response && response.success) {
                setRetreats(response.data || []);
                setTotal(response.totalItems || 0);
                setTotalPages(response.totalPages || 1);
                setError(null);
            } else {
                setRetreats([]);
                setError(response?.message || "Failed to load retreats data.");
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || "Failed to fetch retreats. Please try again later.");
            setRetreats([]);
        } finally {
            setLoading(false);
        }
    }, [limit]);

    const fetchCategoriesList = useCallback(async () => {
        try {
            const response = await categoryActiveApi();
            if (response && response.success) setCategories(response.data || []);
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    }, []);

    const fetchSubCategoriesList = useCallback(async (catId) => {
        try {
            if (!catId) { setSubCategories([]); setSelectedSubCategories([]); return; }
            const response = await subCategoryActiveApiWithoutPagination(catId);
            if (response && response.success) setSubCategories(response.data || []);
            else setSubCategories([]);
        } catch (err) {
            console.error("Error fetching sub-categories:", err);
            setSubCategories([]);
        }
    }, []);

    useEffect(() => {
        const catId = selectedCategory ? selectedCategory.id : '';
        const subIds = selectedSubCategories.map(s => s.id).join(',');
        const cId = selectedCenter?.id || '';
        fetchRetreats(searchTerm, statusFilter, locationSearch, cId, page, catId, subIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchRetreats]);

    useEffect(() => { fetchCategoriesList(); }, [fetchCategoriesList]);

    const fetchSuggestions = useMemo(
        () => debounce(async (searchQuery) => {
            try {
                setSuggestionsLoading(true);
                const response = await getActiveCenters(1, 10, searchQuery);
                if (response && response.success) {
                    setCenterSuggestions(response.data || []);
                } else {
                    setCenterSuggestions([]);
                }
            } catch (err) {
                console.error("Error fetching center suggestions:", err);
                setCenterSuggestions([]);
            } finally {
                setSuggestionsLoading(false);
            }
        }, 300),
        []
    );

    useEffect(() => { fetchSuggestions(''); }, [fetchSuggestions]);

    const checkScroll = useCallback(() => {
        if (tableContainerRef.current && !loading) {
            const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
            setShowScrollHint(scrollWidth > clientWidth && scrollLeft < 20);
        } else {
            setShowScrollHint(false);
        }
    }, [loading]);

    useEffect(() => { const t = setTimeout(checkScroll, 150); return () => clearTimeout(t); }, [retreats, loading, checkScroll]);
    useEffect(() => { window.addEventListener('resize', checkScroll); return () => window.removeEventListener('resize', checkScroll); }, [checkScroll]);

    const handleSearchChange = (e) => setSearchTerm(e.target.value);
    const handleStatusChange = (e) => setStatusFilter(e.target.value);
    const handleLocationChange = (e) => setLocationSearch(e.target.value);
    const handleCenterNameInputChange = (e, value) => {
        const q = value || '';
        setCenterNameSearch(q);
        fetchSuggestions(q);
    };
    const handleCategoryChange = (e, newValue) => {
        setSelectedCategory(newValue);
        setSelectedSubCategories([]);
        setSubCategories([]);
        if (newValue?.id) fetchSubCategoriesList(newValue.id);
    };
    const handleSubCategoriesChange = (e, newValue) => setSelectedSubCategories(newValue);

    const handleApplyFilters = () => {
        setPage(1);
        const catId = selectedCategory ? selectedCategory.id : '';
        const subIds = selectedSubCategories.map(s => s.id).join(',');
        const cId = selectedCenter?.id || '';
        fetchRetreats(searchTerm, statusFilter, locationSearch, cId, 1, catId, subIds);
    };

    const handleResetFilters = () => {
        setSearchTerm(''); setStatusFilter(''); setLocationSearch('');
        setSelectedCenter(null); setCenterNameSearch(''); setSelectedCategory(null);
        setSelectedSubCategories([]); setSubCategories([]); setPage(1);
        fetchRetreats('', '', '', '', 1, '', '');
    };

    const handlePageChange = (e, newPage) => {
        setPage(newPage);
        const catId = selectedCategory ? selectedCategory.id : '';
        const subIds = selectedSubCategories.map(s => s.id).join(',');
        const cId = selectedCenter?.id || '';
        fetchRetreats(searchTerm, statusFilter, locationSearch, cId, newPage, catId, subIds);
    };

    const handleActionClick = (e, retreat) => { setAnchorEl(e.currentTarget); setSelectedRetreat(retreat); };
    const handleActionClose = () => { setAnchorEl(null); setSelectedRetreat(null); };
    const handleViewDetails = () => {
        if (!selectedRetreat) return;
        navigate('/retreat/view-details', { state: { retreat: selectedRetreat } });
        handleActionClose();
    };
    const handleViewBookings = () => {
        if (!selectedRetreat) return;
        navigate(`/retreat/bookings/${selectedRetreat.id}`, { state: { retreatTitle: selectedRetreat.title } });
        handleActionClose();
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'published': return { bgcolor: '#E8F5E8', color: '#2E7D32' };
            case 'draft': return { bgcolor: '#FFF3E0', color: '#F57C00' };
            case 'rejected': return { bgcolor: '#FFEBEE', color: '#C62828' };
            default: return { bgcolor: '#F5F5F5', color: '#666' };
        }
    };

    const getEventTypeColor = (eventType) => {
        switch (eventType?.toLowerCase()) {
            case 'training': return { bgcolor: '#E3F2FD', color: '#1976D2' };
            case 'workshop': return { bgcolor: '#F3E5F5', color: '#7B1FA2' };
            case 'retreat': return { bgcolor: '#E8F5E8', color: '#2E7D32' };
            default: return { bgcolor: '#F5F5F5', color: '#666' };
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>

            {/* Header */}
            <Typography sx={{ color: '#222', fontWeight: 600, fontSize: '1.5rem', mb: 2.5 }}>
                Retreats Management
            </Typography>

            {/* ── Filter Bar ── */}
            <Paper
                elevation={0}
                sx={{
                    border: '1px solid #EBEBEB',
                    borderRadius: '10px',
                    p: '12px 16px',
                    mb: 2.5,
                    bgcolor: '#fff',
                }}
            >
                {/* Row 1: all inputs */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        alignItems: 'center',
                    }}
                >
                    {/* Status */}
                    <FormControl size="small" sx={{ width: 130 }}>
                        <InputLabel sx={{ fontSize: '0.8125rem' }}>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            label="Status"
                            onChange={handleStatusChange}
                            sx={{
                                borderRadius: '8px',
                                height: '36px',
                                fontSize: '0.8125rem',
                                '& fieldset': { borderColor: '#E8E8E8' },
                                '&:hover fieldset': { borderColor: '#FFBD65' },
                                '&.Mui-focused fieldset': { borderColor: '#FFBD65' },
                            }}
                        >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="published">Published</MenuItem>
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Title / Slug */}
                    <TextField
                        placeholder="Title or slug..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        variant="outlined"
                        size="small"
                        sx={{ width: 190, ...inputSx }}
                    />

                    {/* Center Name */}
                    <Autocomplete
                        freeSolo
                        size="small"
                        options={centerSuggestions}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ''}
                        value={selectedCenter}
                        onChange={(e, newValue) => setSelectedCenter(newValue)}
                        loading={suggestionsLoading}
                        inputValue={centerNameSearch}
                        onInputChange={handleCenterNameInputChange}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Center name..."
                                variant="outlined"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {suggestionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                        sx={{ width: 190, ...inputSx }}
                    />

                    {/* Location */}
                    <TextField
                        placeholder="Location..."
                        value={locationSearch}
                        onChange={handleLocationChange}
                        variant="outlined"
                        size="small"
                        sx={{ width: 160, ...inputSx }}
                    />

                    {/* Category */}
                    <Autocomplete
                        size="small"
                        options={categories || []}
                        getOptionLabel={(o) => o.name || ''}
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        isOptionEqualToValue={(o, v) => Number(o.id) === Number(v?.id)}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Category" variant="outlined" />
                        )}
                        sx={{ width: 175, ...inputSx }}
                    />

                    {/* Sub Category */}
                    <Autocomplete
                        multiple
                        size="small"
                        disabled={!selectedCategory}
                        options={subCategories || []}
                        getOptionLabel={(o) => o.name || ''}
                        value={selectedSubCategories}
                        onChange={handleSubCategoriesChange}
                        isOptionEqualToValue={(o, v) => Number(o.id) === Number(v?.id)}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Sub Category" variant="outlined" />
                        )}
                        sx={{ width: 200, ...inputSx }}
                    />

                    {/* Spacer */}
                    <Box sx={{ flex: 1 }} />

                    {/* Buttons */}
                    <Button
                        variant="contained"
                        onClick={handleApplyFilters}
                        sx={{
                            bgcolor: '#FFBD65',
                            color: '#fff',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '0.8125rem',
                            height: '36px',
                            px: 2.5,
                            boxShadow: 'none',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Apply Filters
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleResetFilters}
                        sx={{
                            color: '#888',
                            borderColor: '#E0E0E0',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.8125rem',
                            height: '36px',
                            px: 2,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Reset
                    </Button>
                </Box>
            </Paper>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Table */}
            <Paper
                sx={{ borderRadius: '10px', border: '1px solid #EBEBEB', overflow: 'hidden', position: 'relative' }}
                elevation={0}
            >
                <TableContainer ref={tableContainerRef} onScroll={checkScroll}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                {['S.No', 'Title', 'Slug', 'Center', 'Location', 'Event Type', 'Status', 'Expiry', 'Completed', 'Created', 'Updated', 'Actions'].map((h, i) => (
                                    <TableCell
                                        key={h}
                                        align={i === 11 ? 'right' : 'left'}
                                        sx={{ fontWeight: 700, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.7px', py: 1.5, whiteSpace: 'nowrap' }}
                                    >
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                                        <CircularProgress sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : retreats.length > 0 ? (
                                retreats.map((retreat, idx) => (
                                    <TableRow key={retreat.id} >
                                        <TableCell sx={{ color: '#888', fontSize: '0.8125rem' }}>
                                            {(page - 1) * limit + idx + 1}
                                        </TableCell>

                                        <TableCell sx={{ fontWeight: 500, maxWidth: 200 }}>
                                            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8125rem' }} title={retreat.title}>
                                                {retreat.title || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ maxWidth: 180 }}>
                                            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8125rem', color: '#64748B' }} title={retreat.slug || retreat.center?.slug}>
                                                {retreat.slug || retreat.center?.slug || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ maxWidth: 180 }}>
                                            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8125rem' }} title={retreat.center?.name}>
                                                {retreat.center?.name || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ maxWidth: 200 }}>
                                            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8125rem' }} title={retreat.center?.location}>
                                                {retreat.center?.location || 'N/A'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={retreat.eventType ? retreat.eventType.charAt(0).toUpperCase() + retreat.eventType.slice(1).toLowerCase() : 'N/A'}
                                                size="small"
                                                sx={{ ...getEventTypeColor(retreat.eventType), fontSize: '0.75rem', height: 22 }}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={retreat.status ? retreat.status.charAt(0).toUpperCase() + retreat.status.slice(1).toLowerCase() : 'N/A'}
                                                size="small"
                                                sx={{ ...getStatusColor(retreat.status), fontSize: '0.75rem', height: 22 }}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={retreat.expired ? 'Expired' : 'Active'}
                                                size="small"
                                                sx={{
                                                    bgcolor: retreat.expired ? '#FFEBEE' : '#E8F5E8',
                                                    color: retreat.expired ? '#C62828' : '#2E7D32',
                                                    fontWeight: 500, fontSize: '0.75rem', height: 22
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={retreat.isCompleted ? 'Yes' : 'No'}
                                                size="small"
                                                sx={{
                                                    bgcolor: retreat.isCompleted ? '#E8F5E8' : '#F5F5F5',
                                                    color: retreat.isCompleted ? '#2E7D32' : '#888',
                                                    fontSize: '0.75rem', height: 22
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>
                                                {new Date(retreat.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography sx={{ color: '#888', fontSize: '0.8rem' }}>
                                                {new Date(retreat.updatedAt).toLocaleDateString()}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="right">
                                            <IconButton size="small" onClick={(e) => handleActionClick(e, retreat)} sx={{ color: '#999' }}>
                                                <MoreVertical size={16} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 5, color: '#999', fontSize: '0.875rem' }}>
                                        {searchTerm || locationSearch || centerNameSearch ? 'No retreats match your filters.' : 'No retreats found.'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Scroll fade */}
                {showScrollHint && (
                    <>
                        <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '80px', background: 'linear-gradient(to left, rgba(255,255,255,0.9), rgba(255,255,255,0))', pointerEvents: 'none', zIndex: 5 }} />
                        <Box
                            sx={{
                                position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255,255,255,0.95)', boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
                                borderRadius: '30px', border: '1px solid #FFBD65',
                                display: 'flex', alignItems: 'center', gap: 0.5, py: 0.75, px: 1.5, zIndex: 6, pointerEvents: 'none',
                                animation: 'pulseHint 2.5s infinite ease-in-out',
                                '@keyframes pulseHint': {
                                    '0%,100%': { transform: 'translateY(-50%) scale(1)', opacity: 0.95 },
                                    '50%': { transform: 'translateY(-50%) translateX(-5px) scale(1.03)', opacity: 1 },
                                },
                            }}
                        >
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#FFBD65', letterSpacing: '0.5px' }}>
                                SCROLL TO VIEW MORE
                            </Typography>
                            <ChevronRight size={13} color="#FFBD65" />
                        </Box>
                    </>
                )}
            </Paper>

            {/* Pagination */}
            <GlobalPagination
                page={page}
                pageSize={limit}
                totalRecords={total}
                totalPages={totalPages}
                onPageChange={(newPage) => {
                    handlePageChange(null, newPage);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onPageSizeChange={(newSize) => {
                    setLimit(newSize);
                    setPage(1);
                    const catId = selectedCategory ? selectedCategory.id : '';
                    const subIds = selectedSubCategories.map(s => s.id).join(',');
                    const cId = selectedCenter?.id || '';
                    fetchRetreats(searchTerm, statusFilter, locationSearch, cId, 1, catId, subIds, newSize);
                }}
                loading={loading}
            />

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleActionClose}
                PaperProps={{
                    sx: { boxShadow: '0px 4px 20px rgba(0,0,0,0.08)', borderRadius: '8px', border: '1px solid #F0F0F0', minWidth: 170 }
                }}
            >
                <MenuItem onClick={handleViewDetails} sx={{ py: 1.2 }}>
                    <ListItemIcon sx={{ color: '#FFBD65', minWidth: '34px !important' }}>
                        <LayoutDashboard size={16} />
                    </ListItemIcon>
                    <ListItemText primary="View Details" primaryTypographyProps={{ fontSize: '0.85rem' }} />
                </MenuItem>
                <MenuItem onClick={handleViewBookings} sx={{ py: 1.2 }}>
                    <ListItemIcon sx={{ color: '#34A853', minWidth: '34px !important' }}>
                        <History size={16} />
                    </ListItemIcon>
                    <ListItemText primary="View Bookings" primaryTypographyProps={{ fontSize: '0.85rem' }} />
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default Retreats;